import {useCallback, useEffect, useState} from 'react'

import {useMatrixContext} from '#/state/matrix'

type DirectMap = Record<string, string[]>

export function useDirectRooms() {
  const {client, isConnected} = useMatrixContext()
  const [directMap, setDirectMap] = useState<DirectMap>({})

  useEffect(() => {
    if (!client || !isConnected) return

    const loadDirectMap = () => {
      const event = client.getAccountData('m.direct' as any)
      const content = event?.getContent() as DirectMap | undefined
      setDirectMap(content ?? {})
    }

    loadDirectMap()

    client.on('accountData' as any, loadDirectMap)
    return () => {
      client.removeListener('accountData' as any, loadDirectMap)
    }
  }, [client, isConnected])

  const getExistingDmRoomId = useCallback(
    (matrixUserId: string): string | null => {
      if (!client) return null
      // Read directly from SDK store instead of React state to avoid stale closures
      const event = client.getAccountData('m.direct' as any)
      const content = (event?.getContent() as DirectMap) ?? {}
      const roomIds = content[matrixUserId] ?? []
      for (const roomId of roomIds) {
        const room = client.getRoom(roomId)
        if (room && room.getMyMembership() === 'join') {
          return roomId
        }
      }
      return null
    },
    [client],
  )

  const setDirectRoom = useCallback(
    async (matrixUserId: string, roomId: string) => {
      if (!client) return
      // Re-read fresh to avoid race conditions
      const event = client.getAccountData('m.direct' as any)
      const current = (event?.getContent() as DirectMap) ?? {}
      const existing = current[matrixUserId] ?? []
      if (existing.includes(roomId)) return

      const updated = {
        ...current,
        [matrixUserId]: [...existing, roomId],
      }
      await (client.setAccountData as any)('m.direct', updated)
    },
    [client],
  )

  const isDirectRoom = useCallback(
    (roomId: string): boolean => {
      return Object.values(directMap).some(roomIds => roomIds.includes(roomId))
    },
    [directMap],
  )

  /** Given a room ID, return the Matrix user ID of the DM partner from m.direct data */
  const getDirectRoomUserId = useCallback(
    (roomId: string): string | null => {
      for (const [matrixUserId, roomIds] of Object.entries(directMap)) {
        if (roomIds.includes(roomId)) {
          return matrixUserId
        }
      }
      return null
    },
    [directMap],
  )

  return {getExistingDmRoomId, setDirectRoom, isDirectRoom, getDirectRoomUserId}
}
